import 'whatwg-fetch';
import 'abortcontroller-polyfill/dist/polyfill-patch-fetch';
import FileSaver from 'file-saver';
import { getCookie, setCookie } from '../utils/cookie';
import { broadcastLogout } from './crossTabActivity';
import { LOG_OUT } from '../containers/App/constants';
import { currentStatusSink } from './requestStatusSink';

function parseJSON(response) {
  return new Promise((resolve) => {
    const { status, ok } = response;

    // No body (e.g. 204 No Content): do not call .json() (it rejects and used to hang the app).
    if (status === 204 || status === 205) {
      return resolve({ status, ok, json: null });
    }

    response
      .json()
      .then((json) => resolve({ status, ok, json }))
      .catch(() => {
        resolve({
          status,
          ok,
          json: ok ? null : { message: `Request failed (${status})` },
        });
      });
  });
}

/* istanbul ignore next */
// Get API URL from environment
const environmentURL = import.meta.env.REACT_APP_API_URL;
export const apiURL = environmentURL || 'http://localhost:8000';
export const REQUEST_ABORTED = 'The user aborted a request.';

export const getRequestOptions = () => ({
  headers: {
    Authorization: getCookie('session_id'),
    'Content-Type': 'application/json',
  },
});
export const logout = () => {
  // Decode the JWT to get userId so other tabs for this user can be logged out.
  // JWT payload is base64url-encoded; replace chars to make it standard base64.
  try {
    const token = getCookie('session_id');
    if (token) {
      const b64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
      const payload = JSON.parse(atob(b64));
      if (payload && payload.id) broadcastLogout(payload.id);
    }
  } catch (_) {
    // Ignore — JWT may be malformed or already cleared; proceed with logout
  }
  // Dispatch cleanup before SPA navigation. The explicit fallback covers
  // application routes that intentionally sit outside PrivateRoute.
  // store.js pulls in sagas/socketMiddleware/rootReducer, so it's imported
  // lazily here to avoid dragging that weight into every module that imports
  // this file for its request helpers.
  import('../store').then(({ default: store, history }) => {
    store.dispatch({ type: LOG_OUT });
    setTimeout(() => history.replace('/login'), 0);
  });
};

/**
 * In-flight token renewal promise to prevent concurrent renewal attempts
 * If multiple requests fail with 401 simultaneously, they'll all wait for the same renewal
 */
let inFlightRenewal: Promise<{
  token: string,
  tokenTTL: number,
} | null> | null = null;

/**
 * Attempts to extend the user session by calling the extendSession endpoint.
 * Returns the new token and TTL if successful, null if it fails.
 * Uses direct fetch to avoid circular dependencies with the request functions.
 *
 * Implements singleton pattern: if a renewal is already in progress, returns the same promise
 * to prevent race conditions and duplicate API calls when multiple requests fail with 401 simultaneously.
 *
 * Now includes retry logic with exponential backoff for better resilience against transient network issues.
 */
const attemptTokenRenewal = async (): Promise<{
  token: string,
  tokenTTL: number,
} | null> => {
  // If a renewal is already in progress, return the same promise
  if (inFlightRenewal) {
    return inFlightRenewal;
  }

  const currentToken = getCookie('session_id');
  if (!currentToken) {
    return null;
  }

  // Create the renewal promise and store it
  inFlightRenewal = (async () => {
    const maxAttempts = 3;

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      try {
        const response = await fetch(`${apiURL}/users/extendSession`, {
          method: 'GET',
          headers: {
            Authorization: currentToken,
            'Content-Type': 'application/json',
          },
        });

        if (response.ok) {
          const result = await response.json();
          if (result.token && result.tokenTTL) {
            setCookie('session_id', result.token, result.tokenTTL);
            return { token: result.token, tokenTTL: result.tokenTTL };
          }
          // Response OK but missing token/tokenTTL - treat as error
          // Fall through to backoff logic
        }

        // 401 means the session is definitively dead — no need to retry.
        // Other non-OK responses (5xx, network blip) are worth retrying.
        if (response.status === 401) {
          return null;
        }

        // If response not ok OR missing data, retry with exponential backoff
        if (attempt < maxAttempts - 1) {
          const backoffMs = Math.pow(2, attempt) * 1000; // 1s, 2s, 4s
          await new Promise((resolve) => setTimeout(resolve, backoffMs));
        }
      } catch (error) {
        // Network error - retry with backoff if not last attempt
        if (attempt < maxAttempts - 1) {
          const backoffMs = Math.pow(2, attempt) * 1000; // 1s, 2s, 4s
          await new Promise((resolve) => setTimeout(resolve, backoffMs));
        }
      }
    }

    // All attempts failed
    return null;
  })().finally(() => {
    // Clear the in-flight promise when done (success or failure)
    inFlightRenewal = null;
  });

  return inFlightRenewal;
};

export const postAsync = async (url, body, options) => {
  const result = await requestAPIAsync(url, body, {
    method: 'POST',
    ...options,
  });
  return result;
};

export const requestAPIAsync = async (url, body, options) => {
  const makeRequest = async (token) => {
    const requestOptions = {
      method: options?.method || 'GET',
      headers: {
        Authorization: token || getCookie('session_id'),
        'Content-Type': 'application/json',
      },
      body: body || undefined,
    };

    const fullUrl = (options?.isOtherApi ? '' : apiURL) + url;
    return fetch(fullUrl, requestOptions);
  };

  try {
    let response = await makeRequest();

    const contentLength = response.headers.get('content-length');
    const contentType = response.headers.get('content-type');

    if (!response.ok) {
      if (response.status === 401) {
        const renewedToken = await attemptTokenRenewal();
        if (renewedToken) {
          response = await makeRequest(renewedToken.token);
          if (response.status === 401) {
            logout();
            const clonedResponse = response.clone();
            const errorText = await clonedResponse
              .text()
              .catch(() => 'Unauthorized');
            throw new Error(`HTTP ${response.status}: ${errorText}`);
          }
        } else {
          logout();
          const clonedResponse = response.clone();
          const errorText = await clonedResponse
            .text()
            .catch(() => 'Unauthorized');
          throw new Error(`HTTP ${response.status}: ${errorText}`);
        }
      }

      if (!response.ok) {
        try {
          const errorText = await response.text();
          throw new Error(`HTTP ${response.status}: ${errorText}`);
        } catch (readError) {
          throw new Error(`HTTP ${response.status}`);
        }
      }
    }

    if (contentLength === '0' || contentLength === null) {
      return {};
    }

    if (!contentType || !contentType.includes('application/json')) {
      const textResult = await response.text();
      return { message: textResult };
    }

    try {
      const jsonResult = await response.json();
      return jsonResult;
    } catch (jsonError) {
      const textFallback = await response.text();
      return { message: textFallback };
    }
  } catch (fetchError) {
    throw fetchError;
  }
};

export const requestAPI = (url: string, body: any, options?: Object) => {
  // Captured before the first await so the status reaches the tracked call that started this request.
  const statusSink = currentStatusSink();
  const makeRequest = (token?: string) => {
    const requestOptions: Object = {
      headers: {
        Authorization: token || getCookie('session_id'),
        'Content-Type': 'application/json',
      },
      body: undefined,
    };
    if (body) {
      requestOptions.body = body;
    }
    return fetch((options.isOtherApi ? '' : apiURL) + url, {
      ...requestOptions,
      ...options,
    });
  };

  // $FlowFixMe
  return new Promise(async (resolve, reject) => {
    try {
      let response = await makeRequest();
      let parsedResponse = await parseJSON(response);

      if (
        parsedResponse.status === 401 ||
        parsedResponse.json?.statusCode === 401
      ) {
        const renewedToken = await attemptTokenRenewal();
        if (renewedToken) {
          response = await makeRequest(renewedToken.token);
          parsedResponse = await parseJSON(response);
          if (
            parsedResponse.status === 401 ||
            parsedResponse.json?.statusCode === 401
          ) {
            logout();
            statusSink?.(parsedResponse.status);
            const errorResponse =
              parsedResponse.json?.message ||
              parsedResponse.json?.error ||
              'Unauthorized';
            return reject(errorResponse);
          }
        } else {
          logout();
          statusSink?.(parsedResponse.status);
          const errorResponse =
            parsedResponse.json?.message ||
            parsedResponse.json?.error ||
            'Unauthorized';
          return reject(errorResponse);
        }
      }

      if (parsedResponse.ok) {
        return resolve(parsedResponse.json);
      }

      if (
        options?.fullError &&
        parsedResponse.json &&
        typeof parsedResponse.json === 'object'
      ) {
        statusSink?.(parsedResponse.status);
        return reject(parsedResponse.json);
      }

      statusSink?.(parsedResponse.status);
      const errorResponse =
        parsedResponse.json?.message ||
        parsedResponse.json?.error ||
        (typeof parsedResponse.json === 'string'
          ? parsedResponse.json
          : null) ||
        `Request failed (${parsedResponse.status})`;
      return reject(errorResponse);
    } catch (error) {
      return reject({ networkError: error.message });
    }
  });
};
export const del = (url: string, body?: any, options?: Object) => {
  return requestAPI(url, body, { method: 'DELETE', ...options });
};
export const get = (url: string, options?: Object) => {
  return requestAPI(url, false, { method: 'GET', ...options });
};
export const post = (url: string, body?: any, options?: Object) => {
  return requestAPI(url, body, { method: 'POST', ...options });
};
export const put = (url: string, body?: any, options?: Object) => {
  return requestAPI(url, body, { method: 'PUT', ...options });
};
export const patch = (url: string, body?: any, options?: Object) => {
  return requestAPI(url, body, { method: 'PATCH', ...options });
};

export const getDownload = (
  url: string,
  fileName: string,
  options?: Object,
) => {
  return downloadAPI(url, fileName, false, { method: 'GET', ...options });
};

export const postDownload = (
  url: string,
  fileName: string,
  body?: any,
  options?: Object,
) => {
  return downloadAPI(url, fileName, body, { method: 'POST', ...options });
};

export const downloadAPI = (
  url: string,
  fileName: string,
  body: any,
  options?: Object,
) => {
  const makeRequest = (token?: string) => {
    const requestOptions = {
      headers: {
        Authorization: token || getCookie('session_id'),
        'Content-Type': 'application/json',
      },
      body: undefined,
    };
    if (body) {
      requestOptions.body = body;
    }
    const isAbsolute = /^https?:\/\//i.test(url);
    const fullUrl = isAbsolute
      ? url
      : (options?.isOtherApi ? '' : apiURL) + url;
    return fetch(fullUrl, { ...requestOptions, ...options });
  };

  // $FlowFixMe
  return new Promise(async (resolve, reject) => {
    try {
      let response = await makeRequest();

      // Handle 204 early: no content to read or save
      if (response.status === 204) {
        return resolve({ __noContent: true });
      }

      if (response.status === 401) {
        // Attempt to renew token before logging out
        const renewedToken = await attemptTokenRenewal();
        if (renewedToken) {
          // Retry the request with the new token
          response = await makeRequest(renewedToken.token);
          // If retry also fails with 401, then logout
          if (response.status === 401) {
            logout();
            try {
              const message = await response.json();
              return reject(message.error || 'Unauthorized');
            } catch {
              return reject('Unauthorized');
            }
          }
        } else {
          // Token renewal failed, logout
          logout();
          try {
            const message = await response.json();
            return reject(message.error || 'Unauthorized');
          } catch {
            return reject('Unauthorized');
          }
        }
      }

      if (response.status === 500 || response.status === 400) {
        const message = await response.json().catch(() => ({}));
        if (options?.fullError) {
          return reject(message);
        }
        return reject(message.message || message.error || 'Server error');
      }

      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        const text = await response.text();
        try {
          const json = text ? JSON.parse(text) : {};
          return resolve(json);
        } catch (e) {
          return resolve({});
        }
      }

      const blob = await response.blob();
      let resolvedFileName = fileName;
      const contentDisposition = response.headers.get('Content-Disposition');
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="([^"]+)"/);
        if (match && match[1]) resolvedFileName = match[1];
      }
      const guessExtFromMime = (ct) => {
        if (!ct) return '';
        const lower = ct.toLowerCase();
        if (lower.includes('application/zip')) return 'zip';
        if (lower.includes('text/csv') || lower.includes('application/csv'))
          return 'csv';
        if (
          lower.includes(
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          )
        )
          return 'xlsx';
        if (lower.includes('application/pdf')) return 'pdf';
        return '';
      };
      let adjustedFileName = resolvedFileName;
      const desiredExt = guessExtFromMime(blob.type);
      if (desiredExt) {
        const lowerName = adjustedFileName.toLowerCase();
        if (!lowerName.endsWith(`.${desiredExt}`)) {
          adjustedFileName = adjustedFileName.replace(/\.[a-z0-9]+$/i, '');
          adjustedFileName = `${adjustedFileName}.${desiredExt}`;
        }
      }
      FileSaver.saveAs(blob, adjustedFileName);
      resolve(blob);
    } catch (error) {
      return reject({ networkError: error.message });
    }
  });
};

// TODO: Deprecate
export function request(url: string, options?: Object) {
  // $FlowFixMe
  return new Promise(async (resolve, reject) => {
    try {
      let response = await fetch(url, options);
      let parsedResponse = await parseJSON(response);

      if (
        parsedResponse.status === 401 ||
        parsedResponse.json?.statusCode === 401
      ) {
        // Attempt to renew token before logging out
        const renewedToken = await attemptTokenRenewal();
        if (renewedToken) {
          // Retry the request with the new token
          const retryOptions = {
            ...options,
            headers: {
              ...options?.headers,
              Authorization: renewedToken.token,
            },
          };
          response = await fetch(url, retryOptions);
          parsedResponse = await parseJSON(response);
          // If retry also fails with 401, then logout
          if (
            parsedResponse.status === 401 ||
            parsedResponse.json?.statusCode === 401
          ) {
            if (!options?.skipLogoutOn401) logout();
            const errorResponse = options?.fullError
              ? parsedResponse.json
              : parsedResponse.json?.message ||
                parsedResponse.json?.error ||
                'Unauthorized';
            return reject(errorResponse);
          }
        } else {
          // Token renewal failed, logout
          if (!options?.skipLogoutOn401) logout();
          const errorResponse = options?.fullError
            ? parsedResponse.json
            : parsedResponse.json?.message ||
              parsedResponse.json?.error ||
              'Unauthorized';
          return reject(errorResponse);
        }
      }

      if (parsedResponse.ok) {
        return resolve(parsedResponse.json);
      }

      const fallbackMessage = `Request failed (${parsedResponse.status})`;
      const errorResponse = options?.fullError
        ? parsedResponse.json ?? { message: fallbackMessage }
        : parsedResponse.json?.message ||
          parsedResponse.json?.error ||
          (typeof parsedResponse.json === 'string'
            ? parsedResponse.json
            : null) ||
          fallbackMessage;

      return reject(errorResponse);
    } catch (error) {
      return reject({
        networkError: error.message,
      });
    }
  });
}
export default request;

// TODO: Deprecate
export const requestToDownload = (url: string, options?: Object) => {
  // $FlowFixMe
  return new Promise((resolve, reject) => {
    fetch(url, options)
      .then((response) => {
        if (
          response.status === 500 ||
          response.status === 400 ||
          response.status === 404
        ) {
          return response.json().then((error) => {
            return reject(error.message ?? error.error);
          });
        }
        return resolve(response.blob());
      })
      .catch((error) =>
        reject({
          networkError: error.message,
        }),
      );
  });
};
